<template>
  <NuxtLayout name="error">
    <main>
      <PflegeSectionText :heading="userFriendlyStatusMsg" heading-tag="h1" text-align="center">
        <template v-if="normalizedError.statusCode === 404">
          <p v-if="errorPageType === 'curabox'">
            Es tut uns leid, aber die Seite, die Sie suchen, konnte leider nicht gefunden werden.
            Keine Sorge, hier sind einige nützliche Links, die Ihnen helfen könnten:
          </p>
          <p v-else>
            Es tut uns leid, aber die Seite, die Sie suchen, konnte leider nicht gefunden werden.
          </p>
        </template>
        <template v-else>
          <p>
            Es tut uns leid, aber es ist ein Problem aufgetreten. Bitte versuchen Sie es später
            erneut oder versuchen Sie die Startseite aufzurufen:
          </p>
          <p>
            <PflegeButton label="Zur Startseite" variant="secondary" @click="handleError()" />
            : Gehen Sie zurück zur Startseite und beginnen Sie von dort aus.
          </p>
          <p>
            Wir arbeiten daran, die Angelegenheit zu klären und entschuldigen uns für eventuelle
            Unannehmlichkeiten.
            <br />
            <br />
            Vielen Dank für Ihr Verständnis.
          </p>
        </template>
        <template v-if="normalizedError.statusCode === 404 && errorPageType === 'curabox'" #actions>
          <div class="error-page-footer-content">
            <template v-if="slug[0] === 'pflege'">
              <PflegeButton
                label="Zur Startseite"
                title="Gehen Sie zurück zur Startseite und beginnen Sie von dort aus."
                @click="handleError('/pflege')"
              />
              <PflegeButton
                label="Zur Kontaktseite"
                title="Falls Sie Unterstützung benötigen, kontaktieren Sie uns gerne."
                @click="handleError('/pflege/kontakt')"
              />
            </template>
            <template v-else-if="slug[0] === 'inko'">
              <PflegeButton
                label="Zur Startseite"
                title="Gehen Sie zurück zur Startseite und beginnen Sie von dort aus."
                @click="handleError('/inko')"
              />
              <PflegeButton
                label="Zur Kontaktseite"
                title="Falls Sie Unterstützung benötigen, kontaktieren Sie uns gerne."
                @click="handleError('/inko/kontakt')"
              />
            </template>

            <template v-else>
              <div class="links-wrapper">
                <b>curabox Pflege</b>
                <div class="return-link-holder">
                  <a
                    title="Gehen Sie zurück zur Startseite und beginnen Sie von dort aus."
                    @click="handleError('/pflege')"
                  >
                    Zur curabox Pflege Startseite
                  </a>
                </div>
                <div class="return-link-holder">
                  <a
                    title="Falls Sie Unterstützung benötigen, kontaktieren Sie uns gerne."
                    @click="handleError('/pflege/kontakt')"
                  >
                    Zur curabox Pflege Kontaktseite
                  </a>
                </div>
              </div>

              <PflegeDivider class="error-page-divider" border-color="neutral-30" />

              <div class="links-wrapper">
                <b>curabox Inko</b>
                <div class="return-link-holder">
                  <a
                    title="Gehen Sie zurück zur Startseite und beginnen Sie von dort aus."
                    @click="handleError('/inko')"
                  >
                    Zur curabox Inko Startseite
                  </a>
                </div>

                <div class="return-link-holder">
                  <a
                    title="Falls Sie Unterstützung benötigen, kontaktieren Sie uns gerne."
                    @click="handleError('/inko/kontakt')"
                  >
                    Zur curabox Inko Kontaktseite
                  </a>
                </div>
              </div>
            </template>
          </div>
        </template>
      </PflegeSectionText>
      <aside>
        <span>Fehlercode:</span>
        <b>{{ normalizedError.statusCode }}</b>
      </aside>
    </main>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { PflegeSectionText, PflegeButton, PflegeDivider } from '@webcarelbj/shared-components'
import type { NuxtError } from '#app'

const errorPageType = (await usePageConfig()).errorPage

const props = defineProps<{ error: NuxtError }>()

/*
  clear error and redirect
*/
// Ensure slug is always a string array
const slug = [useRoute().params.slug].flat()
const handleError = (route = '') =>
  clearError({ redirect: route })

/*
  error normalizing, if error is captured in message-field
*/
const event = useRequestEvent()!
const normalizedError = { ...props.error }
try {
  const errObjInMsg = JSON.parse(props.error.message)
  normalizedError.statusCode = errObjInMsg.status ?? normalizedError.statusCode
  normalizedError.message = errObjInMsg.response ?? normalizedError.message
  normalizedError.statusMessage = errObjInMsg.message ?? normalizedError.statusMessage
} catch (e) {
  // SyntaxError comes from JSON.parse and is not interesting for us
  if (!(e instanceof SyntaxError)) {
    console.error(e)
  }
}

/*
  generate user friendly title
*/
const userFriendlyStatusMsg =
  normalizedError.statusCode === 404
    ? 'Seite nicht gefunden'
    : 'Fehler - Es ist ein Problem aufgetreten'
useSeoMeta({
  title: userFriendlyStatusMsg,
})

setResponseStatus(event, normalizedError.statusCode, normalizedError.statusMessage)
</script>

<style scoped lang="scss">
main {
  position: relative;
  padding: 3rem;
  max-width: var(--max-width);
  margin: 4rem auto;
  box-shadow: 0 0.25rem 0.5rem 0 #689f9f20;
  border-radius: 1rem;
  background: rgb(234 243 243);
  background: linear-gradient(150deg, rgb(234 243 243 / 70%) 0%, rgb(246 235 230 / 50%) 100%);
  background-size: 150% 150%;
  animation: bg 15s infinite ease;
}

@media (prefers-reduced-motion) {
  main {
    animation: none;
  }
}

@keyframes bg {
  0% {
    background-position: 50% 0%;
  }

  50% {
    background-position: 25% 100%;
  }

  100% {
    background-position: 50% 0%;
  }
}

pre {
  overflow: auto;
}

.error-page-footer-content {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  width: 100%;
  padding-top: 1rem;
}

.links-wrapper {
  min-width: 100%;
}

.return-link-holder {
  min-width: 100%;
}

.return-link-holder a {
  display: inline-block;
  cursor: pointer;
}

.error-page-divider {
  width: calc(80% - 4rem);
}

button {
  border: none;
  background: none;
  cursor: pointer;
  background-color: #fff;
  border-radius: 0.5rem;
  padding: 0.5rem 1.5rem;
  transition: background-color var(--time1) ease-in-out;
}

button:is(:hover, :focus) {
  background-color: #fff8;
}

aside {
  position: absolute;
  right: 0.5rem;
  bottom: 0.25rem;
  font-size: 0.5rem;
  opacity: 0.5;
}
</style>
